// LeadDetailCenter — the 10-tab center column of the Ringy-style Lead Detail.
// Row 1: Notes · SMS · Activity Log · Call History · Email
// Row 2: Calendar/Appts · Video Meetings · Call Scripts · Documents · AI Summary
//
// Notes is the default tab because when an agent picks a lead back up,
// prior context (callback times, objections, family details) is what they
// need to see first. The right-rail Notes card has been demoted to a
// small "jump to Notes" summary chip.

import React, { useState } from 'react';
import {
  StickyNote, MessageSquare, Activity, Phone, Mail, Calendar, Video, ClipboardList, FileText, Sparkles,
} from 'lucide-react';
import NotesTab from './tabs/NotesTab.jsx';
import SmsTab from './tabs/SmsTab.jsx';
import ActivityLogTab from './tabs/ActivityLogTab.jsx';
import CallHistoryTab from './tabs/CallHistoryTab.jsx';
import EmailTab from './tabs/EmailTab.jsx';
import CalendarTab from './tabs/CalendarTab.jsx';
import VideoTab from './tabs/VideoTab.jsx';
import CallScriptsTab from './tabs/CallScriptsTab.jsx';
import DocumentsTab from './tabs/DocumentsTab.jsx';
import AiSummaryTab from './tabs/AiSummaryTab.jsx';

const TABS = [
  { id: 'notes',     label: 'Notes',          icon: StickyNote,    row: 1 },
  { id: 'sms',       label: 'SMS',            icon: MessageSquare, row: 1 },
  { id: 'activity',  label: 'Activity Log',   icon: Activity,      row: 1 },
  { id: 'calls',     label: 'Call History',   icon: Phone,         row: 1 },
  { id: 'email',     label: 'Email',          icon: Mail,          row: 1 },
  { id: 'calendar',  label: 'Calendar',       icon: Calendar,      row: 2 },
  { id: 'video',     label: 'Video Meetings', icon: Video,         row: 2 },
  { id: 'scripts',   label: 'Call Scripts',   icon: ClipboardList, row: 2 },
  { id: 'documents', label: 'Documents',      icon: FileText,      row: 2 },
  { id: 'ai-summary', label: 'AI Summary',    icon: Sparkles,      row: 2 },
];

export default function LeadDetailCenter({ leadId, lead, messages, calls, emailMessages, onChange, onCreateAppointment }) {
  // Notes opens by default — that's the primary reason to open the lead.
  const [tab, setTab] = useState('notes');

  function TabButton({ t }) {
    const Icon = t.icon;
    const active = tab === t.id;
    return (
      <button
        onClick={() => setTab(t.id)}
        aria-pressed={active}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition
          ${active
            ? 'bg-civix-purple/25 text-white border border-civix-purple/50'
            : 'text-white/60 hover:text-white/90 border border-transparent'}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {t.label}
      </button>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="space-y-1.5 mb-3">
        <div className="flex flex-wrap gap-1.5">
          {TABS.filter((t) => t.row === 1).map((t) => <TabButton key={t.id} t={t} />)}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TABS.filter((t) => t.row === 2).map((t) => <TabButton key={t.id} t={t} />)}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === 'notes' && <NotesTab leadId={leadId} lead={lead} onChange={onChange} />}
        {tab === 'sms' && <SmsTab leadId={leadId} lead={lead} messages={messages} onChange={onChange} />}
        {tab === 'activity' && <ActivityLogTab leadId={leadId} />}
        {tab === 'calls' && <CallHistoryTab calls={calls} leadId={leadId} />}
        {tab === 'email' && <EmailTab leadId={leadId} emailMessages={emailMessages} onChange={onChange} />}
        {tab === 'calendar' && <CalendarTab leadId={leadId} onCreateAppointment={onCreateAppointment} />}
        {tab === 'video' && <VideoTab lead={lead} />}
        {tab === 'scripts' && <CallScriptsTab lead={lead} />}
        {tab === 'documents' && <DocumentsTab lead={lead} leadId={leadId} onChange={onChange} />}
        {tab === 'ai-summary' && <AiSummaryTab leadId={leadId} />}
      </div>
    </div>
  );
}
